import { AlertTriangle, Infinity as InfinityIcon, TrendingDown, TrendingUp } from 'lucide-react';
import type { ResourceAvailability } from '@obraplan/core/domain/planning/weekly-board';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Q04 — quem tem saldo · Q05 — quem está sobrecarregado.
 *
 * As duas perguntas ficam lado a lado porque quem abre a tela está tentando resolver uma
 * com a outra: tirar horas de quem sobrou e dar a quem tem folga. Recurso sem limite
 * declarado aparece em separado, e dizendo isso com todas as letras — não é ocioso nem
 * sobrecarregado, é sem teto (RN-047).
 */
export function AvailabilityPanel({ availability }: { availability: ResourceAvailability }) {
  // `grid-cols-1` no celular: sem ela a coluna é `auto`, e o nome longo de uma empreiteira
  // ("Escoramento e fôrmas — Lima & Filhos Carpintaria") alargava a página inteira para 443 px,
  // porque o `truncate` só corta dentro de uma coluna que pode encolher.
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="size-4 text-(--color-state-ok)" aria-hidden="true" />
            Quem tem saldo
          </CardTitle>
          <CardDescription>Capacidade ainda não planejada nesta semana.</CardDescription>
        </CardHeader>
        <CardContent>
          {availability.withBalance.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">
              Ninguém com saldo — toda a capacidade da semana já está planejada.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {availability.withBalance.map((line) => (
                <li
                  key={line.resource.resourceId}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{line.resource.name}</p>
                    <p className="truncate text-xs text-(--color-content-muted)">
                      {line.resource.detail}
                    </p>
                  </div>
                  <Badge tone="ok">
                    {Hours.ofQuarters(line.balance.balanceQuarterHours ?? 0).format()} livres
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingDown className="size-4 text-(--color-state-danger)" aria-hidden="true" />
            Quem está sobrecarregado
          </CardTitle>
          <CardDescription>
            Planejado acima da capacidade — cada caso tem justificativa registrada.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {availability.overloaded.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">
              Ninguém acima da capacidade nesta semana.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {availability.overloaded.map((line) => (
                <li
                  key={line.resource.resourceId}
                  className="flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{line.resource.name}</p>
                    <p className="truncate text-xs text-(--color-content-muted)">
                      {line.resource.detail}
                    </p>
                  </div>
                  <Badge tone="danger" icon={<AlertTriangle aria-hidden="true" />}>
                    {Hours.ofQuarters(-(line.balance.balanceQuarterHours ?? 0)).format()} acima
                  </Badge>
                </li>
              ))}
            </ul>
          )}

          {availability.unlimited.length > 0 && (
            <div className="mt-4 border-t border-(--color-border-subtle) pt-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs text-(--color-content-muted)">
                <InfinityIcon className="size-3.5" aria-hidden="true" />
                Sem capacidade declarada — não entram no cálculo de saldo
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {availability.unlimited.map((line) => (
                  <li key={line.resource.resourceId}>
                    <Badge tone="neutral">{line.resource.name}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
